import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import dayjs from 'dayjs';

import { getCellClassForGroups } from '@shared/utils/ag-cell-class-for-groups';
import { Utils } from '@shared/utils/utils';

import {
  currencyFormatter,
  defaultHeaderClass,
  defaultMinWidth,
  defaultWidthForMoneyColumns,
  InMonthAdjustmentGridConfigContext,
} from './ima-grid-config-context';
import { ImaTableGroups } from '../enum/ima-table-gropes';

export const imaOverallBudgetColumnGroup = (
  ctx: InMonthAdjustmentGridConfigContext
): ColGroupDef => {
  return {
    children: getOverallBudgetColumns(ctx),
    groupId: ImaTableGroups.OVERALL_BUDGET,
    headerClass: ['text-center', 'header-marker'],
    headerName: 'Overall Budget',
    suppressStickyLabel: true,
  };
};

const getOverallBudgetColumns = (ctx: InMonthAdjustmentGridConfigContext): ColDef[] => {
  return [
    {
      field: 'activity_type',
      filter: true,
      hide: true,
    },
    {
      cellClass: getCellClassForGroups,
      field: 'display_label',
      headerClass: defaultHeaderClass,
      headerName: 'Label',
      headerTooltip: 'Label',
      minWidth: defaultMinWidth,
      width: 100,
    },
    {
      cellClass: getCellClassForGroups,
      colId: 'uom',
      field: 'uom',
      filter: true,
      filterParams: {
        showTooltips: true,
      },
      headerClass: defaultHeaderClass,
      headerName: 'Unit of Measure',
      headerTooltip: 'Unit of Measure',
      hide: !ctx.flags.showUOM,
      menuTabs: ['filterMenuTab'],
      minWidth: defaultMinWidth,
      suppressMenu: false,
      tooltipField: 'uom',
      valueFormatter: (params) => params.value || '',
      width: 120,
    },
    {
      cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
      field: 'units',
      filter: 'agNumberColumnFilter',
      headerClass: defaultHeaderClass,
      headerName: 'Units',
      headerTooltip: 'Units',
      minWidth: defaultMinWidth,
      valueFormatter: (params) => Utils.decimalFormatter(params.value),
      width: 95,
    },
    ...tmaAmounts,
    actualsThroughColumn(ctx),
    {
      cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
      field: 'total_remaining',
      filter: 'agNumberColumnFilter',
      headerClass: defaultHeaderClass,
      headerName: 'Remaining ($)',
      headerTooltip: 'Remaining ($)',
      minWidth: defaultMinWidth,
      valueFormatter: currencyFormatter,
      width: defaultWidthForMoneyColumns,
    },
  ];
};

const actualsThroughColumn = (ctx: InMonthAdjustmentGridConfigContext) => {
  const headerName = `Actuals through ${dayjs(ctx.selectedMonth).subtract(1, 'month').format('MMM YYYY')} ($)`;

  return {
    cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
    field: 'actuals_to_date',
    filter: 'agNumberColumnFilter',
    headerClass: defaultHeaderClass,
    headerName,
    headerTooltip: headerName,
    minWidth: defaultMinWidth,
    valueFormatter: currencyFormatter,
    width: 170,
  } satisfies ColDef;
};

const tmaAmounts: ColDef[] = [
  {
    cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
    field: 'tma_unit_cost',
    filter: 'agNumberColumnFilter',
    headerClass: defaultHeaderClass,
    headerName: 'Unit Cost',
    headerTooltip: 'Unit Cost',
    minWidth: defaultMinWidth,
    valueFormatter: currencyFormatter,
    width: defaultWidthForMoneyColumns,
  },
  {
    cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
    field: 'total_amount',
    filter: 'agNumberColumnFilter',
    headerClass: defaultHeaderClass,
    headerName: 'Current (LRE)',
    headerTooltip: 'Current (LRE)',
    minWidth: defaultMinWidth,
    valueFormatter: currencyFormatter,
    width: defaultWidthForMoneyColumns,
  },
];
